﻿import React, { useDebugValue, useEffect, useState } from "react";
import { Container, Box, Paper, Typography } from "@mui/material";
import { ShepTab, ShepTabs } from "@/components/GenericComponents/ShepTab";
import { useDispatch, useSelector } from "react-redux";
import { selectViewDataPopupData, fetchViewDataPopupData, setViewDataPopupData } from "@/features/homepage/homeSlice";
import { selectWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import AllowanceChargeCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/AllowanceChargeCatalogTab";
import CarrierCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/CarrierCatalogTab";
import CustomerCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/CustomerCatalogTab";
import ItemCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/ItemCatalogTab";
import ItemUOMCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/ItemUOMCatalogTab";
import ShipToCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/ShipToCatalogTab";
import WarehouseCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/WarehouseCatalogTab";
import TaxGroupCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/TaxGroupCatalogTab";
import "./MapOnFly.css";
import LoadingBar from "@/components/shared/LoadingBar";
import {
  selectMapOnFlyInfo,
  selectIsMapOnFlyOpen,
  resetCatalogFilter,
  selectMissingCrossRefCounts,
  setMissingCrossRefCounts,
  setMappedGridFlag,
} from "@/features/connections/crossReferenceSlice";
import { selectAllRecords } from "@/features/homepage/workflowManagerSlice";

//just for order of tabs, not the enum in backend
const mapOnFly = {
  Customer: 0,
  ShipTo: 1,
  TaxGroup: 2,
  Item: 3,
  UOM: 4,
  Warehouse: 5,
  Carrier: 6,
  MiscCharges: 7,
};

const UnmappedBadge = ({ unmappedNum }) => {
  return (
    <Container className="unmappedBadge">
      {unmappedNum >= 100 ? "99+" : unmappedNum}
    </Container>
  );
};

const MapOnFly = () => {
  const mapOnFlyInfo = useSelector(selectMapOnFlyInfo);
  const isOnFly = useSelector(selectIsMapOnFlyOpen);
  const [updateOrViewTabValue, setUpdateOrViewTabValue] = useState(0);
  const currentOperationId = useSelector(selectWorkflow);  

  //for fetching data for the Raw Data tab (reuses the function from the three dot menu)
  const rawDataInfo = useSelector(selectViewDataPopupData);
  const allRecords = useSelector(selectAllRecords);
  const targetID = useSelector(state => state.tempInfo.workflowManager.selectedRecord.id);
  //where workflowRecordId matches
  const rowData = allRecords.find(record => record.WorkflowRecordId === targetID);

  const dispatch = useDispatch();

  //call thunk
  useEffect(() => {
    try {
      //clear old raw data when popup is opened
      dispatch(
        setViewDataPopupData({ 
          data: {rawDataSource: "default"} 
        })
      );

      dispatch(
        fetchViewDataPopupData({
          viewType: "(Raw)",
          sourceReferenceId: rowData.SourceArtifactDictionaryDataId,
          targetReferenceId: rowData.TargetArtifactDictionaryDataId,
          operationId: currentOperationId,
          connectionPairId: rowData.ConnectionPairId,
        })
      );
    } catch (e) {
      console.error("DISPATCH FAILED", e);
    }
  }, []);

  const isLoading = !rawDataInfo?.rawDataSource || rawDataInfo?.rawDataSource === "default";

  const getSortedUnmappedCrossReferences = () => {
    const value = [
      ...mapOnFlyInfo?.crossReferenceInfo.UnmappedCrossReferences,
    ]?.sort((a, b) => mapOnFly[a.ReferenceType] - mapOnFly[b.ReferenceType]);
    return value;
  };

  const [catalogTabValue, setCatalogTabValue] = useState(() => {
    const tabType = getSortedUnmappedCrossReferences()[0]?.ReferenceType; //gets first type if any
    return mapOnFly[tabType] ?? 0;
  }); //match tab to enum
  const unmappedCrossReferenceCount = useSelector(selectMissingCrossRefCounts);

  useEffect(() => {
    dispatch(resetCatalogFilter({}));
  }, [catalogTabValue, mapOnFlyInfo]);
  useEffect(() => {
    const crossRefInfo = mapOnFlyInfo.crossReferenceInfo;
    dispatch(
      setMissingCrossRefCounts({
        counts: fetchMissingCrossReferenceCounts({ crossRefInfo }),
      })
    );
  }, [mapOnFlyInfo]);
  const handleCatalogTabChange = (e, newValue) => {
    dispatch(setMappedGridFlag({ mappedGridLoaded: false }));
    setCatalogTabValue(newValue);
  };
  const handleUpdateOrViewTabChange = (e, newValue) => {
    setUpdateOrViewTabValue(newValue);
  };

  const isTabDisabled = (tabValue) => {
    // find where first error is and disable following tabs
    const tabNames = Object.keys(mapOnFly);
    let isDisabled = false;
    for (let i = 0; i < tabValue; i++) {
      const tabName = tabNames[i];
      if (unmappedCrossReferenceCount[tabName] > 0) {
        isDisabled = true;
        break;
      }
    }
    if (isDisabled) return true;
    //see if tabs are disabled due to document type Artifact Process
    const currentTabName = tabNames[tabValue];
    const referenceTypeNum =
      mapOnFlyInfo.crossReferenceInfo.CrossReferenceTypes[currentTabName];
    const match = mapOnFlyInfo.crossReferenceInfo.IdentifiedCatalogs.find(
      (x) => x.ReferenceType === referenceTypeNum
    );
    return match === null || match === undefined;
  };

  const renderComponent = () => {
    const showTabTwo = (updateOrViewTabValue === 1);
    return (
      <>
        <Box
          sx={{
            flex: "auto",
            marginLeft: "5px",
            width: "99%",
            backgroundColor: "var(--disabled)",
          }}
        >
          <ShepTabs
            sx={{
              margin: "40px 30px 30px 0",
              borderBottom: "1px solid var(--primary-main)",
            }}
            variant="primary"
            value={updateOrViewTabValue}
            onChange={handleUpdateOrViewTabChange}
            aria-label="update or view tabs"
          >
            <ShepTab variant="primary" key="1" label={"Update"} />
            <ShepTab variant="primary" key="2" label={"Raw Data"} />
          </ShepTabs>

          <Paper>
            <Typography variant="h4">Connection Pair Details</Typography>
            <Container
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "start",
                flexDirection: "column",
              }}
            >
              <Container
                sx={{
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "baseline",
                  flexDirection: "row",
                }}
              >
                <Typography variant="h6" sx={{ paddingRight: "10px" }}>
                  Name:
                </Typography>
                <Typography>
                  {mapOnFlyInfo.crossReferenceInfo.ConnectionPairName}
                </Typography>
              </Container>
              {isOnFly && (
                <Container
                  sx={{
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "baseline",
                    flexDirection: "row",
                  }}
                >
                  <Typography variant="h6" sx={{ paddingRight: "10px" }}>
                    Reference Id:
                  </Typography>
                  <Typography>
                    {mapOnFlyInfo.crossReferenceInfo.ReferenceId}
                  </Typography>
                </Container>
              )}
            </Container>
          </Paper>

              {/* renders both tabs but only shows the relevant one (to locally save pending changes) */}
            <div style={{display: showTabTwo ? 'none' : 'block'}}>
              <>
                <ShepTabs
                  sx={{
                    margin: "40px 30px 30px 0",
                    borderBottom: "1px solid var(--primary-main)",
                  }}
                  variant="secondary"
                  value={catalogTabValue}
                  onChange={handleCatalogTabChange}
                  aria-label="catalog tabs"
                >
                  <ShepTab
                    variant="secondary"
                    key={`${mapOnFly.Customer + 1}`}
                    label={"Customer"}
                    disabled={isTabDisabled(mapOnFly.Customer)}
                  />
                  {unmappedCrossReferenceCount.Customer != 0 && (
                    <UnmappedBadge
                      unmappedNum={unmappedCrossReferenceCount.Customer}
                    />
                  )}

                  <ShepTab
                    variant="secondary"
                    key={`${mapOnFly.ShipTo + 1}`}
                    label={"Ship To"}
                    disabled={isTabDisabled(mapOnFly.ShipTo)}
                  />
                  {unmappedCrossReferenceCount.ShipTo != 0 && (
                    <UnmappedBadge
                      unmappedNum={unmappedCrossReferenceCount.ShipTo}
                    />
                  )}
                  <ShepTab
                    variant="secondary"
                    key={`${mapOnFly.TaxGroup + 1}`}
                    label={"Tax Group"}
                    disabled={isTabDisabled(mapOnFly.TaxGroup)}
                  />
                  {unmappedCrossReferenceCount.TaxGroup != 0 && (
                    <UnmappedBadge
                      unmappedNum={unmappedCrossReferenceCount.TaxGroup}
                    />
                  )}
                  <ShepTab
                    variant="secondary"
                    key={`${mapOnFly.Item + 1}`}
                    label={"Item"}
                    disabled={isTabDisabled(mapOnFly.Item)}
                  />
                  {unmappedCrossReferenceCount.Item != 0 && (
                    <UnmappedBadge
                      unmappedNum={unmappedCrossReferenceCount.Item}
                    />
                  )}
                  <ShepTab
                    variant="secondary"
                    key={`${mapOnFly.UOM + 1}`}
                    label={"Item UOM"}
                    disabled={isTabDisabled(mapOnFly.UOM)}
                  />
                  {unmappedCrossReferenceCount.UOM != 0 && (
                    <UnmappedBadge
                      unmappedNum={unmappedCrossReferenceCount.UOM}
                    />
                  )}
                  <ShepTab
                    variant="secondary"
                    key={`${mapOnFly.Warehouse + 1}`}
                    label={"Warehouse"}
                    disabled={isTabDisabled(mapOnFly.Warehouse)}
                  />
                  {unmappedCrossReferenceCount.Warehouse != 0 && (
                    <UnmappedBadge
                      unmappedNum={unmappedCrossReferenceCount.Warehouse}
                    />
                  )}
                  <ShepTab
                    variant="secondary"
                    key={`${mapOnFly.Carrier + 1}`}
                    label={"Carrier"}
                    disabled={isTabDisabled(mapOnFly.Carrier)}
                  />
                  {unmappedCrossReferenceCount.Carrier != 0 && (
                    <UnmappedBadge
                      unmappedNum={unmappedCrossReferenceCount.Carrier}
                    />
                  )}
                  <ShepTab
                    variant="secondary"
                    key={`${mapOnFly.MiscCharges + 1}`}
                    label={"Allowance Charge"}
                    disabled={isTabDisabled(mapOnFly.MiscCharges)}
                  />
                  {unmappedCrossReferenceCount.MiscCharges != 0 && (
                    <UnmappedBadge
                      unmappedNum={unmappedCrossReferenceCount.MiscCharges}
                    />
                  )}
                </ShepTabs>
              </>
                
              <>
                {catalogTabValue === mapOnFly.Customer && <CustomerCatalogTab />}
                {catalogTabValue === mapOnFly.ShipTo && <ShipToCatalogTab />}
                {catalogTabValue === mapOnFly.Item && <ItemCatalogTab />}
                {catalogTabValue === mapOnFly.UOM && <ItemUOMCatalogTab />}
                {catalogTabValue === mapOnFly.Warehouse && <WarehouseCatalogTab />}
                {catalogTabValue === mapOnFly.Carrier && <CarrierCatalogTab />}
                {catalogTabValue === mapOnFly.MiscCharges && <AllowanceChargeCatalogTab />}
                {catalogTabValue === mapOnFly.TaxGroup && <TaxGroupCatalogTab />}
              </>
            </div>


            <div style={{display: showTabTwo ? 'block' : 'none'}}>
              <>
                <Paper
                  sx={{
                    margin: "20px",
                    padding: "20px",
                    height: "900px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: 'auto',
                  }}
                >
                  {isLoading ? (
                    <LoadingBar isDataLoaded={false} secondsToStall={5}></LoadingBar>
                  ) : (
                    <pre 
                      style={{
                      padding: '15px', 
                      maxWidth: '100%', 
                      maxHeight: '900px', 
                      whiteSpace: 'pre-wrap', 
                      fontSize: '20px',
                      }}>
                        {rawDataInfo.rawDataSource}
                      </pre>
                  )}
                </Paper>
              </>
            </div>
        </Box>
      </>
    );
  };

  return <Container>{renderComponent()}</Container>;
};

export default MapOnFly;

export const getRequeueStatus = ({
  unmappedCatalogCounts,
  referenceType,
  catalogInformation,
  identifiedCatalogs,
}) => {
  const keys = Object.keys(unmappedCatalogCounts);
  const otherError = keys
    .filter((x) => x !== referenceType)
    .some((x) => unmappedCatalogCounts[x] !== 0);
  if (otherError) {
    //if active missing mapping in other tab
    return false;
  }

  const sourceCatalogs = catalogInformation?.sourceCatalogs ?? [];
  if (sourceCatalogs?.length === 0) {
    //no more catalogs to map
    return true;
  }

  const mappedCatalogs = catalogInformation?.mappedSourceCatalogs ?? [];

  //if default mapping exists, immediately allow requeue
  if (mappedCatalogs?.filter((x) => x.DefaultMappingType === 0).length > 0) {
    return true;
  }

  //1 to 1 mappings
  const explicitMaps = identifiedCatalogs.filter((x) =>
    mappedCatalogs.some((y) => y.CatalogId === x.SourceCatalogId)
  );
  if (explicitMaps.length === identifiedCatalogs.length) return true;

  if (referenceType === "UOM" || referenceType === "ShipTo") {
    // gets list of identified catalogs that are covered by both explicit and implicit maps

    const parentlessMappedCatalogs = mappedCatalogs.filter(
      (x) => x.DefaultMappingType === 1
    );

    const implicitMaps = identifiedCatalogs.filter((x) =>
      parentlessMappedCatalogs.some(
        (y) => x.SourceCode === y.CatalogReference.split(",")[1]
      )
    );
    const uniqueHandledCatalogs = [
      ...new Set([...explicitMaps, ...implicitMaps]),
    ];
    if (uniqueHandledCatalogs.length === identifiedCatalogs.length) return true;
  }

  return false;
};

const fetchMissingCrossReferenceCounts = ({ crossRefInfo }) => {
  const totalCounts = {
    Customer: getNumberOfMissingReferences({
      referenceType: "Customer",
      crossRefInfo,
    }),
    ShipTo: getNumberOfMissingReferences({
      referenceType: "ShipTo",
      crossRefInfo,
    }),
    Item: getNumberOfMissingReferences({
      referenceType: "Item",
      crossRefInfo,
    }),
    UOM: getNumberOfMissingReferences({
      referenceType: "UOM",
      crossRefInfo,
    }),
    Warehouse: getNumberOfMissingReferences({
      referenceType: "Warehouse",
      crossRefInfo,
    }),
    Carrier: getNumberOfMissingReferences({
      referenceType: "Carrier",
      crossRefInfo,
    }),
    MiscCharges: getNumberOfMissingReferences({
      referenceType: "MiscCharges",
      crossRefInfo,
    }),
    TaxGroup: getNumberOfMissingReferences({
      referenceType: "TaxGroup",
      crossRefInfo,
    }),
  };
  return totalCounts;
};

const getNumberOfMissingReferences = ({ referenceType, crossRefInfo }) => {
  if (!referenceType) return 0;

  const unmappedCatalogs = crossRefInfo.UnmappedCrossReferences.filter(
    (x) => x.ReferenceType === referenceType
  );
  const getCount = unmappedCatalogs.length;
  return getCount;
};
